import React from 'react';
import {Box, Chip, Divider, InputLabel, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {ManageClientService, ServiceValidationStatusEnum} from '@store/clients/services';

import {Sizes} from '@constants/sizes.constants';

import TextFieldComponent from '@components/inputs/textField/TextField.component';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import {indexationService} from '@store/settings/indexation';
import AsyncSelectComponent from '@components/inputs/asyncSelect/AsyncSelect.component';
import {map} from 'rxjs';
import {UnitOfMeasure} from '@store/settings/serviceKinds';
import SelectComponent from '@components/inputs/select/Select.component';

interface ServiceFormProps {
  errors: { [key: string]: any };
  service: ManageClientService;
  updateService: (data: ManageClientService) => void;
}

const ServiceForm = (props: ServiceFormProps) => {
  const {errors, service, updateService} = props;

  const {t} = useTranslation();

  return <>
    <Stack py="20px" spacing={2}>
      <Typography fontSize={16} fontWeight="700">
        {t('clients.details.contractedServices.manage.service')}
      </Typography>
      <Stack spacing={2}>
        <TextFieldComponent
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('clients.details.contractedServices.manage.name')}
          placeholder={t('global.writeSomething')}
          handleChange={(clientWasteName) => updateService({clientWasteName})}
          value={service.clientWasteName || ''}
          error={errors.clientWasteName}
          required/>
        <TextFieldComponent
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('clients.details.contractedServices.manage.idDatahub')}
          placeholder={t('global.writeSomething')}
          handleChange={(value) => updateService({datahubSpec: {idDatahub: value}})}
          value={service.datahubSpec?.idDatahub || ''}
          required/>
        <SelectComponent
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('clients.details.contractedServices.manage.execUnitName')}
          placeholder={t('global.selectOption')}
          handleChange={(value) => updateService({execUnitName: value})}
          value={service.execUnitName}
          items={UnitOfMeasure.selectItems()}
        />
        <Stack direction="row" spacing={2}>
          <InputLabel sx={{width: Sizes.drawerInputLabelWidth}}>
            {t('clients.details.contractedServices.manage.buyDiscount')}*
          </InputLabel>
          <Box flex={0.5}>
            <NumberTextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              placeholder={t('global.writeSomething')}
              pattern={new RegExp(/^(\d*|\d+)[,.]?\d*$/)}
              endAdornment={<Typography fontSize={14} fontWeight="400">%</Typography>}
              startAdornment={<Typography fontSize={14} fontWeight="400">A</Typography>}
              handleChange={(pctBuyDiscountRule) => updateService({pctBuyDiscountRule})}
              value={service.pctBuyDiscountRule}
              error={errors.pctBuyDiscountRule}
              required/>
          </Box>
          <Box flex={0.5}>
            <NumberTextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              placeholder={t('global.writeSomething')}
              pattern={new RegExp(/^(\d*|\d+)[,.]?\d*$/)}
              endAdornment={<Typography fontSize={14} fontWeight="400">%</Typography>}
              startAdornment={<Typography fontSize={14} fontWeight="400">V</Typography>}
              handleChange={(pctSellDiscountRule) => updateService({pctSellDiscountRule})}
              value={service.pctSellDiscountRule}
              error={errors.pctSellDiscountRule}
              required/>
          </Box>

        </Stack>

        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <InputLabel sx={{width: Sizes.drawerInputLabelWidth}}>
            {t('clients.details.contractedServices.manage.validity')}*
          </InputLabel>
          <Box flex={0.5}>
            <DatePickerComponent
              placeholder={t('global.selectDate')}
              value={service.validFromDate}
              inputWidth={120}
              handleChange={(validFromDate) =>
                updateService({
                  validFromDate: validFromDate,
                })
              }
              error={errors.validFromDate}/>
          </Box>
          <Box flex={0.5}>
            <DatePickerComponent
              placeholder={t('global.selectDate')}
              value={service.validToDate}
              inputWidth={120}
              minDate={service.validFromDate}
              handleChange={(validToDate) =>
                updateService({
                  validToDate: validToDate,
                })
              }/>
          </Box>
        </Stack>
        <AsyncSelectComponent
          getOptions={() => indexationService.getIndexations().pipe(map((indexations) => indexations.map((indexation) => ({
            value: indexation.id,
            label: `${indexation.name} - ${indexation.currentIndexationRate?.rateInPercent}%`
          }))))}
          label={t('clients.details.contractedServices.manage.buyIndexation')}
          labelWidth={Sizes.drawerInputLabelWidth}
          handleChange={(indexation) => {
            updateService({buyIndexation: indexation});
            updateService({buyIndexationId: indexation?.value});
          }}
          placeholder={t('global.select')}
          value={service.buyIndexation}
        />
        <AsyncSelectComponent
          getOptions={() => indexationService.getIndexations().pipe(map((indexations) => indexations.map((indexation) => ({
            value: indexation.id,
            label: `${indexation.name} - ${indexation.currentIndexationRate?.rateInPercent}%`
          }))))}
          label={t('clients.details.contractedServices.manage.sellIndexation')}
          labelWidth={Sizes.drawerInputLabelWidth}
          handleChange={(indexation) => {
            updateService({sellIndexation: indexation});
            updateService({sellIndexationId: indexation?.value});
          }}
          placeholder={t('global.select')}
          value={service.sellIndexation}
        />
        <SelectComponent
          labelWidth={Sizes.drawerInputLabelWidth}
          label={t('clients.details.contractedServices.manage.validationStatus')}
          placeholder={t('global.selectOption')}
          handleChange={(value) => updateService({validationStatus: value})}
          value={service.validationStatus}
          items={ServiceValidationStatusEnum.selectItems()}/>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Chip
            label={t('clients.details.contractedServices.manage.canBeRequested')}
            style={{
              backgroundColor: service.canBeRequested ? '#6FD969' : '#F4F6F8',
              color: service.canBeRequested ? '#192133' : '#868F9C',
            }}
            onClick={() => updateService({canBeRequested: !service.canBeRequested})}
          />
          <Chip
            label={t('clients.details.contractedServices.manage.clientInvoicingRequired')}
            style={{
              backgroundColor: service.clientInvoicingRequired ? '#6FD969' : '#F4F6F8',
              color: service.clientInvoicingRequired ? '#192133' : '#868F9C',
            }}
            onClick={() => updateService({clientInvoicingRequired: !service.clientInvoicingRequired})}
          />
          <Chip
            label={t('clients.details.contractedServices.manage.supplierInvoicingRequired')}
            style={{
              backgroundColor: service.supplierInvoicingRequired ? '#6FD969' : '#F4F6F8',
              color: service.supplierInvoicingRequired ? '#192133' : '#868F9C',
            }}
            onClick={() => updateService({supplierInvoicingRequired: !service.supplierInvoicingRequired})}
          />
          <Chip
            label={t('clients.details.contractedServices.manage.intersite')}
            style={{
              backgroundColor: service.intersite ? '#6FD969' : '#F4F6F8',
              color: service.intersite ? '#192133' : '#868F9C',
            }}
            onClick={() => updateService({intersite: !service.intersite})}
          />
        </Stack>
      </Stack>
    </Stack>
    <Divider variant="fullWidth" orientation="horizontal"/>
  </>;
};

export default ServiceForm;
